import { __read, __assign, __rest } from '../../external/tslib/tslib.es6.mjs.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { FocusScope } from 'react-aria';
import styles from './Modal.module.scss.js';
import { CloseButton } from './closeButton/CloseButton.js';
import { useModalCount } from './useModalCount/useModalCount.js';

var ANIMATION_TIME = parseInt(styles.animationTime);
var Modal = function (_a) {
    var _b;
    var children = _a.children, className = _a.className, style = _a.style, onClose = _a.onClose, afterClose = _a.afterClose, isOpened = _a.isOpened, _c = _a.size, size = _c === void 0 ? 'm' : _c, isPortalDisabled = _a.isPortalDisabled, ariaLabelledBy = _a["aria-labelledby"], ariaDescribedBy = _a["aria-describedby"], fullscreenVariant = _a.fullscreenVariant;
    var _d = useModalCount(), subtract = _d.subtract, add = _d.add;
    // Save page scrollY before modal is opened and restore it after close
    // It fixes auto scroll to input fields on focus in android
    var _e = __read(useState(0), 2), pageScrollY = _e[0], setPageScrollY = _e[1];
    var _f = __read(useState(false), 2), hasMounted = _f[0], setHasMounted = _f[1];
    useEffect(function () {
        setHasMounted(true);
    }, []);
    useEffect(function () {
        if (isOpened) {
            add();
        }
        return function () {
            if (isOpened) {
                setTimeout(function () {
                    subtract();
                }, ANIMATION_TIME);
            }
        };
    }, [isOpened, add, subtract]);
    useEffect(function () {
        if (isOpened && fullscreenVariant === 'default') {
            setTimeout(function () {
                setPageScrollY(window.scrollY);
                window.scrollTo(0, 0);
            }, ANIMATION_TIME);
        }
    }, [isOpened, fullscreenVariant]);
    useEffect(function () {
        if (!isOpened && pageScrollY) {
            window.scrollTo(0, pageScrollY);
        }
    }, [isOpened, pageScrollY]);
    var handleKeyDown = function (event) {
        if (event.key !== 'Escape' || !onClose) {
            return;
        }
        event.stopPropagation();
        onClose();
    };
    var modalElement = (jsx(CSSTransition, __assign({ in: isOpened, timeout: ANIMATION_TIME, classNames: {
            enter: styles.enter,
            enterActive: styles.enterActive,
            exit: styles.exit,
            exitActive: styles.exitActive,
        }, unmountOnExit: true, onExited: function () {
            if (!isOpened && afterClose) {
                afterClose();
            }
        } }, { children: jsx(FocusScope, __assign({ contain: true, restoreFocus: true, autoFocus: true }, { children: jsxs("div", __assign({ role: "presentation", className: styles.modal, onKeyDown: handleKeyDown, tabIndex: -1 }, { children: [jsx("div", { "aria-hidden": true, className: styles.overlay, onClick: onClose }), jsxs("div", __assign({ role: "dialog", "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-modal": "true", className: clsx(styles.wrapper, className, (_b = {},
                            _b[styles.fullscreen] = fullscreenVariant === 'default',
                            _b[styles.fullscreenMobile] = fullscreenVariant === 'mobile-only',
                            _b[styles.xs] = size === 'xs',
                            _b[styles.small] = size === 's',
                            _b[styles.medium] = size === 'm',
                            _b[styles.large] = size === 'l',
                            _b)), style: style }, { children: [onClose && jsx(CloseButton, { className: styles.closeButton, onClick: onClose }), children] }))] })) })) })));
    if (isPortalDisabled) {
        return modalElement;
    }
    return typeof window !== 'undefined' && hasMounted
        ? createPortal(modalElement, document.querySelector('#portals'))
        : null;
};
Modal.Header = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (jsx("div", __assign({ className: clsx(styles.header, className) }, props)));
};
Modal.Content = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (jsx("div", __assign({ className: clsx(styles.content, className) }, props)));
};
Modal.Actions = function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (jsx("div", __assign({ className: clsx(styles.actions, className) }, props)));
};
Modal.CloseButton = CloseButton;

export { Modal };
